import clsx from 'clsx'
import {FC, KeyboardEvent, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {Link} from 'react-router-dom'
import {useAuth} from '../../../auth'
import {MY_BUSINESSES_PATH, hasManyBusinesses} from '../../../auth/core/postLoginPath'
import {BusinessOption, businessMatchesSearch} from './BusinessOption'
import {readBusinessChangeError} from './BusinessSelect'
import {businessOptionData, useBusinessSwitcherLabels} from './businessSwitcherLabels'
import {businessTabUrl, switchBusiness} from './switchBusiness'
import './BusinessQuickSwitcher.scss'

export const BUSINESS_SWITCHER_OPEN_EVENT = 'megapay:business-switcher:open'

/** Opens the quick switcher from anywhere (the header chip, a menu). */
export const openBusinessSwitcher = () =>
  window.dispatchEvent(new Event(BUSINESS_SWITCHER_OPEN_EVENT))

/**
 * Whether the signed-in user chooses among businesses here: a user of more than one (not a support session, which is
 * held to one business, nor the administrator, who picks on the pages that follow a business).
 */
export const useCanSwitchBusiness = () => {
  const {currentUser} = useAuth()
  return hasManyBusinesses(currentUser)
}

/**
 * «رفتن به کسب‌وکار دیگر» (the user, 2026-10-26): Ctrl+K on any page (⌘K on a Mac) opens a search over the user's
 * businesses; Enter opens the highlighted one in this tab, Ctrl+Enter in a new tab, and the other tabs keep theirs.
 */
export const BusinessQuickSwitcher: FC = () => {
  const {currentUser} = useAuth()
  const canSwitch = useCanSwitchBusiness()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [active, setActive] = useState(0)
  const [changing, setChanging] = useState<number>()
  const [error, setError] = useState('')
  const businesses = currentUser?.cpl
  const labels = useBusinessSwitcherLabels(open ? businesses : undefined)
  const currentId = Number(currentUser?.cpi || 0)

  useEffect(() => {
    if (!canSwitch) return
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        (event.key === 'k' || event.key === 'K' || event.code === 'KeyK')
      ) {
        event.preventDefault()
        setOpen(true)
      }
    }
    const onOpen = () => setOpen(true)
    window.addEventListener('keydown', onKey)
    window.addEventListener(BUSINESS_SWITCHER_OPEN_EVENT, onOpen)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener(BUSINESS_SWITCHER_OPEN_EVENT, onOpen)
    }
  }, [canSwitch])

  useEffect(() => {
    if (!open) {
      setSearch('')
      setActive(0)
      setError('')
    }
  }, [open])

  const options = useMemo(() => {
    const all = (businesses || []).map((company) => businessOptionData(company, labels[company.id]))
    return all
      .filter((option) => businessMatchesSearch(option, search))
      .sort((left, right) =>
        left.id === currentId
          ? -1
          : right.id === currentId
          ? 1
          : left.name.localeCompare(right.name, 'fa')
      )
  }, [businesses, labels, search, currentId])

  if (!canSwitch) return null

  const choose = (id: number | undefined, newTab: boolean) => {
    if (!id || changing !== undefined) return
    if (newTab) {
      window.open(businessTabUrl(id), '_blank', 'noopener')
      setOpen(false)
      return
    }
    if (id === currentId) {
      setOpen(false)
      return
    }
    setChanging(id)
    setError('')
    switchBusiness(id)
      .then((switched) => {
        if (!switched) setChanging(undefined)
      })
      .catch((switchError) => {
        setChanging(undefined)
        setError(readBusinessChangeError(switchError))
      })
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => Math.min(index + 1, options.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      choose(options[active]?.id, event.ctrlKey || event.metaKey)
    }
  }

  return (
    <Modal
      show={open}
      onHide={() => setOpen(false)}
      centered
      aria-labelledby='mp-business-switcher-title'
      className='mp-business-switcher'
    >
      <Modal.Header closeButton closeLabel='بستن'>
        <Modal.Title id='mp-business-switcher-title' as='h2' className='fs-4'>
          <i className='las la-exchange-alt me-2 text-primary' aria-hidden='true' />
          رفتن به کسب‌وکار دیگر
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <>
          <input
            type='search'
            className='form-control'
            placeholder='نام، برچسب یا کد اقتصادی'
            aria-label='جست‌وجوی کسب‌وکار'
            aria-controls='mp-business-switcher-list'
            aria-activedescendant={
              options[active] ? `mp-business-switcher-${options[active].id}` : undefined
            }
            value={search}
            autoFocus
            onChange={(event) => {
              setSearch(event.target.value)
              setActive(0)
            }}
            onKeyDown={onKeyDown}
          />
          <ul
            id='mp-business-switcher-list'
            className='mp-business-switcher__list'
            role='listbox'
            aria-label='کسب‌وکارهای شما'
          >
            {options.map((option, index) => (
              <li
                key={option.id}
                id={`mp-business-switcher-${option.id}`}
                role='option'
                aria-selected={index === active}
                className={clsx('mp-business-switcher__item', {'is-active': index === active})}
                onMouseEnter={() => setActive(index)}
              >
                <button
                  type='button'
                  className='mp-business-switcher__choose'
                  tabIndex={-1}
                  onClick={() => choose(option.id, false)}
                  disabled={changing !== undefined}
                >
                  {changing === option.id ? (
                    <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
                  ) : null}
                  <BusinessOption business={option} />
                  {option.id === currentId && (
                    <span className='badge badge-light-primary ms-auto'>همین تب</span>
                  )}
                </button>
                <a
                  className='btn btn-icon btn-sm btn-light'
                  href={businessTabUrl(option.id || 0)}
                  target='_blank'
                  rel='noreferrer'
                  tabIndex={-1}
                  aria-label={`باز کردن ${option.name} در تب جدید`}
                  title='باز کردن در تب جدید'
                  onClick={() => setOpen(false)}
                >
                  <i className='las la-external-link-alt fs-4' aria-hidden='true' />
                </a>
              </li>
            ))}
            {options.length === 0 && (
              <li className='text-muted p-4'>کسب‌وکاری با این نام یا کد پیدا نشد.</li>
            )}
          </ul>
        </>
        {error && (
          <div className='text-danger fs-7 mt-3' role='alert'>
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className='justify-content-between'>
        <span className='text-muted fs-8'>
          <kbd>Enter</kbd> ورود در همین تب، <kbd>Ctrl</kbd>+<kbd>Enter</kbd> در تب جدید؛{' '}
          <kbd>Ctrl</kbd>+<kbd>K</kbd> این پنجره را در هر صفحه باز می‌کند.
        </span>
        <Link
          to={MY_BUSINESSES_PATH}
          className='btn btn-sm btn-light-primary'
          onClick={() => setOpen(false)}
        >
          همهٔ کسب‌وکارهای من
        </Link>
      </Modal.Footer>
    </Modal>
  )
}
